'use client';

import { motion } from 'framer-motion';
import { Droplets, Navigation, Sun, Sunrise, Sunset } from 'lucide-react';
import { memo, useMemo } from 'react';

import { mapWeatherCodeToAnimation } from '../../lib/utils/weatherAnimations';
import { getTemperatureLabel } from '../../lib/utils/weatherUtils';
import { ForecastDay } from '../../types/weatherApi';
import AnimatedIcon from './AnimatedIcon';

const TOMSK_TZ = 'Asia/Tomsk';

// один раз создаём форматтеры (не в рендере)
const dtfWeekday = new Intl.DateTimeFormat('ru-RU', { timeZone: TOMSK_TZ, weekday: 'long' });
const dtfDayMonth = new Intl.DateTimeFormat('ru-RU', { timeZone: TOMSK_TZ, day: 'numeric', month: 'long' });
const dtfYmd = new Intl.DateTimeFormat('en-CA', { timeZone: TOMSK_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

function getTomskYmd(d = new Date()) {
  return dtfYmd.format(d); // YYYY-MM-DD
}
function getTomorrowYmd() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return getTomskYmd(d);
}

const getUvIndexClass = (uv: number) => {
  if (uv <= 2) return 'text-green-400';
  if (uv <= 5) return 'text-yellow-400';
  if (uv <= 7) return 'text-orange-400';
  return 'text-red-500';
};

const DetailItem = ({
  label,
  icon: Icon,
  value,
  unit,
  valueClassName = '',
  iconStyle = {},
}: {
  label: string;
  icon: React.ElementType;
  value: string | number;
  unit?: string;
  valueClassName?: string;
  iconStyle?: React.CSSProperties;
}) => (
  <div className="flex flex-col items-center gap-1 text-center">
    <span className="text-xs">{label}</span>
    <div className="flex items-center gap-1 text-sm text-sky-100 font-medium">
      <Icon size={15} className="opacity-80" style={iconStyle} />
      <span className={valueClassName}>
        {value}
        {unit}
      </span>
    </div>
  </div>
);

// Open-Meteo sunrise/sunset часто "YYYY-MM-DDTHH:mm" → берём HH:mm строкой
function formatSunTime(iso?: string) {
  if (!iso || iso.length < 16) return '😢';
  const t = iso.slice(11, 16);
  return /^\d{2}:\d{2}$/.test(t) ? t : '😢';
}

function dateToStableDate(ymd: string) {
  // стабильный Date для форматирования weekday/dayMonth
  return new Date(`${ymd}T00:00:00Z`);
}

function DailyForecastCard({ dayForecast }: { dayForecast: ForecastDay }) {
  const { date, day, astro } = dayForecast;

  const { headerTitle, headerSubtitle } = useMemo(() => {
    const d = dateToStableDate(date);
    const weekday = dtfWeekday.format(d);
    const dayMonth = dtfDayMonth.format(d);

    const today = getTomskYmd();
    const tomorrow = getTomorrowYmd();

    if (date === today) {
      return { headerTitle: `Сегодня (${weekday})`, headerSubtitle: dayMonth };
    }
    if (date === tomorrow) {
      return { headerTitle: `Завтра (${weekday})`, headerSubtitle: dayMonth };
    }
    return { headerTitle: weekday, headerSubtitle: dayMonth };
  }, [date]);

  const maxTemp = day.maxtemp_c ?? 0;
  const minTemp = day.mintemp_c ?? 0;

  const temperatureLabel = getTemperatureLabel(maxTemp);
  const isCold = maxTemp <= -15;

  const windSpeedMps = Number.isFinite(day.maxwind_kph) ? (day.maxwind_kph / 3.6).toFixed(1) : '0.0';
  const windDeg = Number.isFinite(day.wind_dir as number) ? (day.wind_dir as number) : 0;

  const animationPath = mapWeatherCodeToAnimation(day.condition.code);

  const precipMm = Number.isFinite(day.totalprecip_mm) ? day.totalprecip_mm : 0;

  return (
    <motion.div
      className={`${
        isCold ? 'bg-blue-700/70' : 'bg-sky-600/70'
      } backdrop-blur-sm p-4 rounded-xl shadow-lg text-white hover:bg-sky-500/90 transition-all duration-200 ease-in-out flex flex-col justify-between dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:backdrop-blur-sm dark:border dark:border-slate-700 min-h-[460px]`}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <div className="text-center">
        <div className="h-12 flex text-main flex-col justify-center">
          <p className="font-bold text-lg md:text-xl text-main capitalize">{headerTitle}</p>
          <p className="text-sm text-sky-200">{headerSubtitle}</p>
        </div>

        <div className="my-2 w-24 h-24 mx-auto relative flex items-center justify-center">
          <AnimatedIcon path={animationPath} />
        </div>

        <div className="flex justify-center items-baseline gap-2 mb-2">
          <p className="text-5xl font-bold text-center dark:text-slate-50">{Math.round(maxTemp)}°</p>
          <p className="text-neutral-300 text-lg text-center mb-2">{Math.round(minTemp)}°</p>
        </div>

        <p className={`text-sm mt-1 mb-2 font-medium ${isCold ? 'text-cyan-200' : 'text-neutral-300'}`}>
          {temperatureLabel}
        </p>

        <p className="text-base capitalize text-center mb-4 dark:text-slate-400">
          {day.condition.text}
        </p>

        {day.daily_chance_of_rain > 0 && (
          <div className="text-sm text-sky-200 h-[20px] mb-2">
            {(day.totalsnow_cm ?? 0) > 0 ||
            day.condition.text.toLowerCase().includes('снег') ||
            maxTemp < -2
              ? 'Снег'
              : 'Дождь'}
            : {day.daily_chance_of_rain}%
          </div>
        )}
      </div>

      <div className="mt-auto pt-4 border-t border-white/20 space-y-3">
        <div className="flex justify-around items-start">
          <DetailItem icon={Droplets} value={precipMm.toFixed(1)} unit="мм" label="Осадки" />
          <DetailItem
            icon={Navigation}
            value={windSpeedMps}
            unit=" м/с"
            label="Ветер"
            iconStyle={{ transform: `rotate(${windDeg}deg)` }}
          />
          <DetailItem
            icon={Sun}
            value={Number.isFinite(day.uv) ? day.uv.toFixed(1) : '0.0'}
            label="УФ-индекс"
            valueClassName={getUvIndexClass(day.uv ?? 0)}
          />
        </div>

        {astro && (
          <div className="text-center p-2 mt-2 border-t border-white/10">
            <div className="flex justify-between items-center text-xs pt-1">
              <div className="flex items-center gap-1.5">
                <Sunrise size={14} />
                <span>Восход: {formatSunTime(astro?.sunrise)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Sunset size={14} />
                <span>Закат: {formatSunTime(astro?.sunset)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default memo(DailyForecastCard);
